import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GitBranch, Eye, EyeOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import WorkProcessEditor from "@/components/workprocesses/WorkProcessEditor";

interface Department {
  id: string;
  name: string;
}

interface WorkProcess {
  id: string;
  department_id: string;
  title: string;
  description: string | null;
  flowchart_data: any;
  created_at: string;
  updated_at: string;
  renewed_at: string | null;
  is_active: boolean;
  sort_order: number;
  document_path?: string | null;
  document_name?: string | null;
  document_mime_type?: string | null;
  document_size?: number | null;
}

const WorkProcessesSettings = () => {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [processes, setProcesses] = useState<WorkProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingProcess, setEditingProcess] = useState<WorkProcess | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    loadDepartments();
    loadProcesses();
  }, [selectedDepartment, showInactive]);

  const loadDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadProcesses = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('work_processes')
        .select('*')
        .order('sort_order', { ascending: true });

      if (!showInactive) {
        query = query.eq('is_active', true);
      }

      if (selectedDepartment !== "all") {
        query = query.eq('department_id', selectedDepartment);
      }

      const { data, error } = await query;

      if (error) throw error;
      setProcesses(data || []);
    } catch (error) {
      console.error('Error loading processes:', error);
      toast.error("Fehler beim Laden der Prozesse");
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = (departmentId: string) => {
    return departments.find(d => d.id === departmentId)?.name || 'Unbekannt';
  };

  const openCreateEditor = () => {
    setEditingProcess(null);
    setEditorOpen(true);
  };

  const openEditEditor = (process: WorkProcess) => {
    setEditingProcess(process);
    setEditorOpen(true);
  };

  const handleEditorSave = () => {
    setEditorOpen(false);
    setEditingProcess(null);
    loadProcesses();
  };

  const toggleActive = async (process: WorkProcess) => {
    try {
      const { error } = await supabase
        .from('work_processes')
        .update({ 
          is_active: !process.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', process.id);

      if (error) throw error;
      toast.success(process.is_active ? "Prozess deaktiviert" : "Prozess aktiviert");
      loadProcesses();
    } catch (error) {
      console.error('Error toggling process:', error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const renewProcess = async (process: WorkProcess) => {
    try {
      const { error } = await supabase
        .from('work_processes')
        .update({ 
          renewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', process.id);

      if (error) throw error;
      toast.success("Prozess erneuert");
      loadProcesses();
    } catch (error) {
      console.error('Error renewing process:', error);
      toast.error("Fehler beim Erneuern");
    }
  };

  const deleteProcess = async (process: WorkProcess) => {
    if (!confirm(`Möchten Sie den Prozess "${process.title}" wirklich löschen?`)) return;

    try {
      const { error } = await supabase
        .from('work_processes')
        .delete()
        .eq('id', process.id);

      if (error) throw error;
      toast.success("Prozess gelöscht");
      loadProcesses();
    } catch (error) {
      console.error('Error deleting process:', error);
      toast.error("Fehler beim Löschen");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="h-5 w-5" />
                Arbeitsprozesse verwalten
              </CardTitle>
              <CardDescription>
                Erstellen und verwalten Sie Arbeitsabläufe für verschiedene Abteilungen
              </CardDescription>
            </div>
            <Button onClick={openCreateEditor} className="gap-2">
              <Plus className="h-4 w-4" />
              Neuer Prozess
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Abteilung filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Abteilungen</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant={showInactive ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
              className="gap-2"
            >
              {showInactive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {showInactive ? "Inaktive anzeigen" : "Nur aktive"}
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Wird geladen...</div>
          ) : processes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Keine Prozesse gefunden. Erstellen Sie Ihren ersten Arbeitsprozess.
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titel</TableHead>
                    <TableHead>Abteilung</TableHead>
                    <TableHead>Flowchart</TableHead>
                    <TableHead>Dokument</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead>Erneuert</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processes.map((process) => (
                    <TableRow key={process.id}>
                      <TableCell className="font-medium">{process.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{getDepartmentName(process.department_id)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={process.flowchart_data ? "default" : "secondary"}>
                          {process.flowchart_data ? "Vorhanden" : "Fehlt"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={process.document_name ? "default" : "secondary"}>
                          {process.document_name ? "Vorhanden" : "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(process.created_at), 'dd.MM.yyyy', { locale: de })}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {process.renewed_at 
                          ? format(new Date(process.renewed_at), 'dd.MM.yyyy', { locale: de })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={process.is_active ? "default" : "secondary"}>
                          {process.is_active ? "Aktiv" : "Inaktiv"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => renewProcess(process)}
                            title="Erneuern"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditEditor(process)}
                            title="Bearbeiten"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleActive(process)}
                            title={process.is_active ? "Deaktivieren" : "Aktivieren"}
                          >
                            {process.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteProcess(process)}
                            title="Löschen"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Hinweis</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            Hier erstellen und bearbeiten Sie Arbeitsprozesse mit KI-generierten Flowcharts.
            Aktivierte Prozesse werden auf der <strong>Arbeitsprozesse</strong>-Übersichtsseite 
            für Mitarbeiter der jeweiligen Abteilung angezeigt.
          </p>
        </CardContent>
      </Card>

      {/* Process Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingProcess ? 'Prozess bearbeiten' : 'Neuen Prozess erstellen'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            <WorkProcessEditor
              process={editingProcess}
              departments={departments}
              onSave={handleEditorSave}
              onCancel={() => setEditorOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkProcessesSettings;